import { useMachine } from '@xstate/react';
import { assign, send, spawn } from 'xstate';

import { useAuthCoreMachine } from '@nw/auth-core';
import { Config } from '@nw/shared/config';
import {
  clearAllClientDomainTokens,
  setAccessTokenToDomain,
  setFormsAccessTokenToDomain,
  setFormsRefreshTokenToDomain,
  setPaymentsAccessTokenToDomain,
  setPaymentsRefreshTokenToDomain,
  setRefreshTokenToDomain,
  useGetClientAuthCodeAPI,
  useGetFormsAuthClientAuthDetails,
  useGetPaymentsClientAuthDetails,
  useGetUserProfileAPI
} from '@nw/shared/data-access';
import {
  getAPIErrorMessage,
  INVITE_CODE,
  loadSegmentScript,
  sendGAEventToDataLayer,
  sendSegmentIdentifyEventWithBackendResponse
} from '@nw/shared/utils';

import {
  AUTH_CLIENT_ID,
  CALL_BACK_URL,
  CLIENT_ID,
  FALL_BACK_URL
} from '../../constants/clientBasedLoginConstant';
import {
  userAuthHandlerMachineActions,
  userAuthHandlerMachineEvents,
  userAuthHandlerMachineGuards,
  userAuthHandlerMachineServices
} from '../../constants/userAuthHandlerMachineConstants';
import { UserAuthHandlerMachine } from '../../machines/UserAuthHandlerMachine';
import { useGetClientConfig } from '../../services/useGetClientConfig';
import { ClientConfigDetailDTO } from '../../stores/dtos/clientConfigDetailsDTO';
import ClientConfigDetail from '../../stores/models/ClientConfigDetailModel';
import { getClientConfigDetailTransformer } from '../../stores/transformers/clientConfigDetailTransformer';
import { UserAuthHandlerMachineContext } from '../../types/UserAuthHandlerMachineTypes';
import {
  getRedirectionURLWithAuthCode,
  isValidCallBackURL
} from '../../utils/RedirectionURLUtils';

export type UseUserAuthHandlerMachineReturnType = {
  userAuthHandlerMachineSend: any;
  userAuthHandlerMachineState: any;
  status: {
    clientConfigDetailsApiStatus: string;
  };
  errors: {
    clientConfigDetailsApiError: string;
  };
};

const actions = userAuthHandlerMachineActions;
const services = userAuthHandlerMachineServices;
const guards = userAuthHandlerMachineGuards;
const events = userAuthHandlerMachineEvents;

export const useUserAuthHandlerMachine = (
  localClientConfig?: any,
  shouldFetchFormsAuthDetails = false,
  shouldFetchPaymentAuthDetails = false,
  shouldCallUserProfileDetails = false
): UseUserAuthHandlerMachineReturnType => {
  const { getAuthCoreMachineWithConfig } = useAuthCoreMachine();

  const urlParams = new URLSearchParams(window.location.search);
  const hashString = window.location.hash;

  const {
    getClientConfigAPI,
    status: getClientConfigStatus,
    error: getClientConfigError
  } = useGetClientConfig();

  const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();

  const { getPaymentsClientAuthDetailsAPI: getPaymentsAuthDetail } =
    useGetPaymentsClientAuthDetails();
  const { getFormsAuthClientAuthDetailsAPI } =
    useGetFormsAuthClientAuthDetails();

  const { getUserProfileDataApi } = useGetUserProfileAPI();

  const userAuthHandlerMachineWithConfig = UserAuthHandlerMachine().withConfig({
    actions: {
      [actions.assignLocalConfigClientIDToContext]: assign(
        (context: UserAuthHandlerMachineContext, event) => {
          return {
            clientID: localClientConfig?.client_id || context.clientID || ''
          };
        }
      ),
      [actions.assignQueryParamsToContext]: assign(
        (context: UserAuthHandlerMachineContext, event) => {
          const clientId = urlParams.get(CLIENT_ID) || '';
          const authClientId =
            urlParams.get(AUTH_CLIENT_ID) ||
            urlParams.get(CLIENT_ID) ||
            'ib_user_account_frontend';
          const callBackUrl = urlParams.get(CALL_BACK_URL) || '';
          const fallBackUrl = urlParams.get(FALL_BACK_URL) || '';
          const inviteCode = urlParams.get(INVITE_CODE) || '';
          return {
            clientID: clientId,
            callBackURL: callBackUrl,
            fallBackURL: fallBackUrl,
            inviteCode: inviteCode,
            authClientID: authClientId
          };
        }
      ),
      [actions.assignClientConfigDetailsToContext]: assign(
        (context, event: any) => {
          const clientConfigData: ClientConfigDetailDTO =
            getClientConfigDetailTransformer(event.data);
          const clientConfigDetail: ClientConfigDetail = new ClientConfigDetail(
            clientConfigData
          );

          return {
            clientConfigDetails: clientConfigDetail
          };
        }
      ),
      [actions.assignAuthMachineRefToContext]: assign((context, event) => {
        return {
          authMachineRef: spawn(
            getAuthCoreMachineWithConfig(
              context.values['phoneNumber'],
              context.clientID
            )
          ) as any
        };
      }),
      [actions.assignSelfTransitionToTrue]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          shouldDoSelfTransition: true
        })
      ),
      [actions.assignSelfTransitionToFalse]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          shouldDoSelfTransition: false
        })
      ),
      [actions.assignChildMachineSelfTransitionToTrue]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          shouldDoChildMachineSelfTransition: true
        })
      ),
      [actions.assignChildMachineSelfTransitionToFalse]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          shouldDoChildMachineSelfTransition: false
        })
      ),
      [actions.doChildMachineSelfTransition]: send(
        { type: events.doSelfTransition },
        {
          to: (context: any) => context.authMachineRef
        }
      ),
      [actions.updateIsNewUserInContextToTrue]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          isNewUser: true
        })
      ),
      [actions.updateIsNewUserInContextToFalse]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          isNewUser: false
        })
      ),
      [actions.updateIsOtpSentToUserTrue]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          isOtpSentToUser: true
        })
      ),
      [actions.clearErrorMessageFromContext]: assign(
        (context: UserAuthHandlerMachineContext, event: any) => ({
          errorMessage: ''
        })
      ),
      [actions.assignErrorMessageToContext]: assign(
        (context: UserAuthHandlerMachineContext, event: any) => ({
          errorMessage: getAPIErrorMessage(event.data.message)
        })
      ),
      [actions.setIsAuthButtonLoadingToFalse]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        context.isAuthButtonLoading = false;
      },
      [actions.onSuccessGettingClientAuthCode]: (
        context: UserAuthHandlerMachineContext,
        event: any
      ) => {
        context.authCode = event.data.code;
      },
      [actions.onSuccessGettingPaymentsClientAuthenticationDetails]: (
        context: UserAuthHandlerMachineContext,
        event: any
      ) => {
        setPaymentsAccessTokenToDomain(event.data.access_token);
        setPaymentsRefreshTokenToDomain(event.data.refresh_token);
      },
      [actions.onSuccessGettingPaymentAuthCode]: (
        context: UserAuthHandlerMachineContext,
        event: any
      ) => {
        context.paymentAuthCode = event.data.code;
      },
      [actions.updateIsOtpSentToUserFalse]: assign(
        (context: UserAuthHandlerMachineContext, event) => ({
          isOtpSentToUser: false
        })
      ),
      [actions.assignValuesToContext]: assign(
        (context: UserAuthHandlerMachineContext, event: any) => {
          return {
            values: { ...context.values, ...event.values }
          };
        }
      ),
      [actions.setIsAuthButtonLoadingToTrue]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        context.isAuthButtonLoading = true;
      },
      [actions.setIsUserLoggedInToTrue]: assign(
        (context: UserAuthHandlerMachineContext, event) => {
          return {
            isLoggedIn: true
          };
        }
      ),
      [actions.assignChildContextToParentContext]: assign(
        (context: UserAuthHandlerMachineContext, event: any) => {
          return {
            childContext: event.data
          };
        }
      ),
      [actions.redirectToCallBackUrl]: (
        context: UserAuthHandlerMachineContext,
        event,
        { state }
      ) => {
        const redirectURL = getRedirectionURLWithAuthCode(
          {
            urlParams: urlParams,
            redirectURL: context.callBackURL
          },
          { code: context['authCode'] },
          hashString
        );
        window.location.replace(redirectURL);
      },
      [actions.redirectToFallBackUrl]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        const redirectURL = getRedirectionURLWithAuthCode(
          {
            urlParams: urlParams,
            redirectURL: context.fallBackURL
          },
          { code: context['authCode'] },
          hashString
        );
        window.location.replace(redirectURL);
      },
      [actions.redirectToHomePage]: assign(
        (context: UserAuthHandlerMachineContext, event) => {
          return {
            shouldRedirectToProfilePage: true
          };
        }
      ),
      [actions.handleErrorOnFailureGetClientConfigDetails]: () => {},
      [actions.setLoginAccessAndRefreshToken]: (context, event: any) => {
        clearAllClientDomainTokens();
        setAccessTokenToDomain(event.data.accessToken);
        setRefreshTokenToDomain(event.data.refreshToken);
      },
      [actions.onSuccessOtpVerification]: (context, event) => {
        if (context.isNewUser) {
          sendGAEventToDataLayer({
            event: 'otp_verified'
          });
        } else {
          sendGAEventToDataLayer({
            event: 'otp_verify_login'
          });
        }
      },
      [actions.onSuccessGettingFormsAuthCode]: assign((context, event: any) => {
        return {
          formsAuthCode: event.data.code
        };
      }),
      [actions.onSuccessGettingFormsClientAuthDetails]: (
        context,
        event: any
      ) => {
        setFormsAccessTokenToDomain(event.data.access_token);
        setFormsRefreshTokenToDomain(event.data.refresh_token);
      },
      [actions.onSuccessGettingUserProfileDetails]: assign(
        (context, event: any) => {
          const userProfileDetail = event.data;
          loadSegmentScript();
          sendSegmentIdentifyEventWithBackendResponse(userProfileDetail);
          return {
            userProfileData: userProfileDetail
          };
        }
      )
    },

    services: {
      [services.getClientAuthCode]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        return getClientAuthCodeAPI({ client_id: context.authClientID });
      },
      [services.getPaymentAuthCode]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        return getClientAuthCodeAPI({
          client_id: Config['OTG_PAYMENTS_CLIENT_ID']
        });
      },
      [services.getPaymentClientAuthenticationDetails]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        return getPaymentsAuthDetail({
          client_id: Config['OTG_PAYMENTS_CLIENT_ID'],
          code: context.paymentAuthCode
        });
      },
      [services.getClientConfigDetailsApi]: async (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        if (localClientConfig) {
          return new Promise((resolve) => {
            resolve(localClientConfig);
          });
        } else return getClientConfigAPI({ clientId: context.clientID });
      },
      [services.getFormAuthCode]: (context, event) => {
        return getClientAuthCodeAPI({
          client_id: Config['OTG_FORMS_CLIENT_ID']
        });
      },
      [services.getFormClientAuthenticationDetails]: (context, event) => {
        return getFormsAuthClientAuthDetailsAPI({
          client_id: Config['OTG_FORMS_CLIENT_ID'],
          code: context.formsAuthCode
        });
      },
      [services.getUserProfileDetails]: (context, event) => {
        return getUserProfileDataApi();
      }
    },
    guards: {
      [guards.isCallBackUrlPresent]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        return (
          context.callBackURL !== '' && isValidCallBackURL(context.callBackURL)
        );
      },
      [guards.isFallBackUrlPresent]: (
        context: UserAuthHandlerMachineContext,
        event
      ) => {
        return context.fallBackURL !== '';
      },
      [guards.shouldFetchFormAuthCode]: (context, event) => {
        return shouldFetchFormsAuthDetails;
      },
      [guards.shouldFetchPaymentAuthCode]: (context, event) => {
        return shouldFetchPaymentAuthDetails;
      },
      [guards.shouldCallUserProfileDetails]: (context, event) => {
        return shouldCallUserProfileDetails;
      }
    }
  });

  const [userAuthHandlerMachineState, userAuthHandlerMachineSend] = useMachine(
    userAuthHandlerMachineWithConfig
  );

  return {
    userAuthHandlerMachineSend,
    userAuthHandlerMachineState,
    status: {
      clientConfigDetailsApiStatus: getClientConfigStatus
    },
    errors: {
      clientConfigDetailsApiError: getClientConfigError
    }
  };
};
